import React, { useCallback, useRef, useState } from 'react';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { beginFileDialog } from '../utils/fileDialogGuard';

/** Open the native file picker, guarding any enclosing modal drawer from the
 *  picker's Escape/Cancel leaking through and closing it. */
function openPicker(input: HTMLInputElement | null) {
  beginFileDialog();
  input?.click();
}

export const VALID_FILE_TYPES = [
  'bmp', 'csv', 'doc', 'docx', 'edi', 'gif', 'htm', 'html',
  'jpe', 'jpeg', 'jpg', 'mov', 'mp4', 'mpeg', 'pdf', 'png',
  'tif', 'tiff', 'txt', 'wav', 'xls', 'xlsx',
];

export interface FileDropzoneProps<TRow> {
  // Uploads a single file. Returns the new row (or null) so the parent can act
  // on it once the whole batch finishes. Metadata is intentionally NOT handled
  // here — callers that need it collect it elsewhere (e.g. a post-upload edit
  // dialog) and pass an upload closure that supplies whatever it needs.
  onUpload: (file: File) => Promise<TRow | null>;
  // Fires ONCE per drop/pick after every file in the batch has finished
  // uploading (or failed), with the rows that uploaded successfully.
  onUploaded?: (rows: TRow[]) => void;
  // Optional extra attributes spread onto the outer wrapper (used by Pro to
  // keep its `data-pronumber` hook). Allows arbitrary `data-*` attributes.
  wrapperProps?: React.HTMLAttributes<HTMLDivElement> & {
    [dataAttr: `data-${string}`]: unknown;
  };
}

/**
 * Generic drag-and-drop / click-to-pick file uploader. Owns all DnD events,
 * file-pick handling, batching, busy state and error display. It is entity
 * agnostic — the caller's `onUpload` closure performs the actual API call and
 * supplies any entity ids/metadata, and `TRow` is the row type it returns.
 */
export default function FileDropzone<TRow>({
  onUpload,
  onUploaded,
  wrapperProps,
}: FileDropzoneProps<TRow>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [busy, setBusy] = useState(0); // count of in-flight uploads
  const [error, setError] = useState<string | null>(null);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      setError(null);

      setBusy((n) => n + list.length);
      const uploaded: TRow[] = [];
      for (const file of list) {
        try {
          const row = await onUpload(file);
          if (row) uploaded.push(row);
        } catch (e: any) {
          setError(e?.message || `Upload failed for ${file.name}`);
        } finally {
          setBusy((n) => n - 1);
        }
      }
      if (uploaded.length > 0 && onUploaded) {
        onUploaded(uploaded);
      }
    },
    [onUpload, onUploaded],
  );

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types?.includes('Files')) setDragActive(true);
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) handleFiles(e.target.files);
    e.target.value = '';
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker(inputRef.current);
    }
  };

  return (
    <div className="mb-3" {...wrapperProps}>
      <div
        role="button"
        tabIndex={0}
        aria-label="Drop files here or click to upload"
        aria-busy={busy > 0}
        onClick={() => openPicker(inputRef.current)}
        onKeyDown={onKeyDown}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={[
          'flex flex-col items-center justify-center rounded-md border-2 border-dashed p-8 cursor-pointer transition-colors',
          dragActive
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
            : 'border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-900/40 hover:bg-gray-100 dark:hover:bg-gray-800',
        ].join(' ')}
      >
        <CloudArrowUpIcon className="mb-2 h-12 w-12 text-muted" />
        <div className="text-base font-medium text-gray-800 dark:text-gray-100">
          {busy > 0
            ? `Uploading ${busy} file${busy === 1 ? '' : 's'}…`
            : 'Drag & drop your files here and start uploading'}
        </div>
        <div className="mt-1 text-xs text-muted">
          Or click this box and select files manually
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="sr-only"
          aria-label="Choose files to upload"
          onChange={onPick}
        />
      </div>
      <div className="mt-1 text-[0.6875rem] text-muted">
        Valid file types: {VALID_FILE_TYPES.join(', ')}
      </div>
      {error && (
        <div role="alert" className="mt-2 text-sm text-red-600">
          {error}
        </div>
      )}
    </div>
  );
}
